import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Trophy, Edit2, Play } from 'lucide-react';

const MatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMatch = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/matches/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch match');

      const data = await response.json();
      setMatch(data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-100 text-red-800';
      case 'finished': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!match) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">Match not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/matches')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Matches
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/matches/${id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Edit2 size={18} />
            Edit
          </button>
          {match.status === 'scheduled' && (
            <button
              onClick={() => navigate(`/live/${id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Play size={18} />
              Start Live
            </button>
          )}
        </div>
      </div>

      {/* Match Info Card */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          {/* Status Badge */}
          <div className="flex justify-center mb-4">
            <span className={`px-4 py-1 rounded-full text-sm font-medium ${getStatusColor(match.status)}`}>
              {match.status.toUpperCase()}
            </span>
          </div>

          {/* Teams & Score */}
          <div className="grid grid-cols-3 gap-8 items-center mb-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold">{match.home_team.name}</h2>
              <p className="text-gray-500">Home</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold">
                {match.home_score ?? '-'} : {match.away_score ?? '-'}
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold">{match.away_team.name}</h2>
              <p className="text-gray-500">Away</p>
            </div>
          </div>

          {/* Match Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t">
            <div className="flex items-center gap-2 text-gray-600">
              <Trophy size={18} />
              <span>{match.tournament.name}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar size={18} />
              <span>{new Date(match.match_date).toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin size={18} />
              <span>{match.venue.name}, {match.venue.city}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Events Timeline */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-xl font-semibold">Match Events</h3>
        </div>
        <div className="p-6">
          {!match.events || match.events.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No events recorded</p>
          ) : (
            <div className="space-y-4">
              {match.events.map((event, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50">
                  <div className="w-16 text-center">
                    <span className="font-bold">{event.minute}'</span>
                    {event.half && <div className="text-xs text-gray-500">H{event.half}</div>}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">
                      {event.player ? `${event.player.first_name} ${event.player.last_name}` : 'Team Event'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {event.team.name}
                    </div>
                    {event.description && (
                      <div className="text-sm text-gray-500 mt-1">{event.description}</div>
                    )}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${
                    event.event_type_id === 1 ? 'bg-green-100 text-green-800' :
                    event.event_type_id === 2 ? 'bg-yellow-100 text-yellow-800' :
                    event.event_type_id === 3 ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    Event
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchDetail;
